import type {
  BadgeProps,
  FormProps,
  PopoverContentProps,
} from '../../../../core'
import {
  Badge,
  Button,
  ButtonGroup,
  Command,
  CommandHeader,
  CommandInput,
  CommandItem,
  CommandList,
  Form,
  FormProvider,
  FormTextareaField,
  toast,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  SpinnerIcon,
  Tooltip,
  VisuallyHidden,
} from '../../../../core'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import { useCurrentUser } from '../../../utils/currentUserContext'
import type { HasCapability } from '../../../hooks/useHasUserCapability'
import { useRouterOrgSlug } from '../../../utils/orgSlug'
import { useUpdateEncounterMutation } from '../hooks/useEncounterMutations'
import {
  EncounterEnrichmentStatusSchema,
  OverridableEncounterEnrichmentStatusSchema,
} from '../utils/encounterSchemas'
import { serializeEncounterRouteQueryParams } from '../utils/queryParams'
import { getWorkspaceEncounterHref } from '../utils/workspaceEncounterHrefs'
import {
  isEncounterClosed,
  isEncounterFlaggedForFollowUp,
  isEncounterResolved,
  cn,
} from '@joyful/utils'
import type { components } from '@joyful/openapi/types.gen'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
export interface EncounterEnrichmentStatusBadgeProps extends Omit<
  BadgeProps,
  'children' | 'variant'
> {
  /**
   * @default false
   */
  isTooltipDisabled?: boolean
  paidBy: string | null
  status: components['schemas']['EncounterEnrichmentStatus']
  statusOverride?: components['schemas']['EncounterEnrichmentStatus'] | null
  statusOverrideReason?: string | null
}

export function EncounterEnrichmentStatusBadge({
  isTooltipDisabled = false,
  paidBy,
  status,
  statusOverride = null,
  statusOverrideReason = null,
  ...badgeProps
}: EncounterEnrichmentStatusBadgeProps) {
  let badge: ReactNode

  if (statusOverride === null) {
    const metadata = EncounterEnrichmentStatusSchema.meta(status)
    badge = (
      <Badge variant={metadata.badgeVariant} {...badgeProps}>
        {metadata.title}
      </Badge>
    )
  } else {
    const metadata = EncounterEnrichmentStatusSchema.meta(statusOverride)
    badge = (
      <Badge asChild variant={metadata.badgeVariant} {...badgeProps}>
        <span className="border-tremor-brand-subtle border-l-4">
          {metadata.title}
        </span>
      </Badge>
    )
  }

  return isTooltipDisabled === true || statusOverride === null ? (
    badge
  ) : (
    <Tooltip
      content={
        <EncounterEnrichmentStatusOverrideDescription
          originalStatus={status}
          paidBy={paidBy}
          reason={statusOverrideReason}
        />
      }
    >
      {badge}
    </Tooltip>
  )
}

function EncounterEnrichmentStatusOverrideDescription({
  className,
  originalStatus,
  reason,
  ...props
}: Pick<EncounterEnrichmentStatusBadgeProps, 'paidBy'> & {
  className?: string
  originalStatus: components['schemas']['EncounterEnrichmentStatus']
  reason: string | null
}) {
  return (
    <div
      className={cn(
        'text-tremor-content flex flex-col gap-1 text-[0.7rem]',
        className,
      )}
    >
      <p>
        {`Status override applied. Original status was `}
        <EncounterEnrichmentStatusBadge
          status={originalStatus}
          size="xxs"
          {...props}
        />
      </p>

      {reason === null ? null : (
        <p className="text-tremor-content text-[0.7rem] italic">{reason}</p>
      )}
    </div>
  )
}

export interface EnrichmentStatusOverridePopoverContentProps extends Omit<
  PopoverContentProps,
  'children'
> {
  /**
   * Optional header content rendered inside `<CommandHeader>`.
   */
  commandHeader?: React.ReactNode
  /**
   * @default 'Apply status override…'
   */
  commandInputPlaceholder?: string
  onSelectStatus: (
    status: components['schemas']['EncounterEnrichmentStatus'] | null,
  ) => void
  originalStatus: components['schemas']['EncounterEnrichmentStatus'] | null
  selectedStatus: components['schemas']['EncounterEnrichmentStatus'] | null
  statusOverrideReason?: string | null
}

function EnrichmentStatusOverridePopoverContent({
  commandHeader,
  commandInputPlaceholder = 'Apply status override…',
  onSelectStatus,
  originalStatus,
  selectedStatus,
  statusOverrideReason = null,
  ...popoverContentProps
}: EnrichmentStatusOverridePopoverContentProps) {
  return (
    <PopoverContent
      padding="none"
      size="sm"
      {...popoverContentProps}
      onClick={(event) => {
        event.stopPropagation()
        popoverContentProps.onClick?.(event)
      }}
    >
      {selectedStatus !== null && originalStatus !== null ? (
        <EncounterEnrichmentStatusOverrideDescription
          className="border-b p-2"
          originalStatus={originalStatus}
          paidBy={null}
          reason={statusOverrideReason}
        />
      ) : null}
      <Command>
        {commandHeader ? <CommandHeader>{commandHeader}</CommandHeader> : null}
        <CommandInput placeholder={commandInputPlaceholder} />
        <EnrichmentStatusOverrideCommandList
          onSelectStatus={onSelectStatus}
          selectedStatus={selectedStatus}
        />
      </Command>
    </PopoverContent>
  )
}

export interface EnrichmentStatusOverrideCommandListProps {
  onSelectStatus: (
    status: components['schemas']['EncounterEnrichmentStatus'] | null,
  ) => void
  selectedStatus: components['schemas']['EncounterEnrichmentStatus'] | null
}

export function EnrichmentStatusOverrideCommandList({
  onSelectStatus,
  selectedStatus,
}: EnrichmentStatusOverrideCommandListProps) {
  const hasCapability = useHasUserCapability()

  return (
    <CommandList>
      <CommandItem
        isSelected={selectedStatus === null}
        className="py-2"
        onSelect={() => {
          if (selectedStatus !== null) {
            onSelectStatus(null)
          }
        }}
      >
        No status override
      </CommandItem>

      {OverridableEncounterEnrichmentStatusSchema.definitions.map((status) => {
        const isSelected = selectedStatus === status.value
        const isWriteOffDisabled =
          status.value === 'WRITE_OFF' &&
          hasCapability(
            'encounter.enrichment_status_override.manage.write_off',
          ) === false
        return (
          <CommandItem
            key={status.value}
            value={status.title}
            disabled={isWriteOffDisabled}
            containerClassName="gap-0.5"
            subtitle={
              isWriteOffDisabled
                ? 'Insufficient permissions to apply Write Off override.'
                : undefined
            }
            isSelected={isSelected}
            className="py-1.5"
            onSelect={() => {
              if (isSelected === false) {
                onSelectStatus(status.value)
              }
            }}
          >
            <EncounterEnrichmentStatusBadge
              paidBy={null}
              status={status.value}
              isTooltipDisabled
            />
          </CommandItem>
        )
      })}
    </CommandList>
  )
}

function computeEnrichmentStatusOverrideEnabledRules({
  currentUserId,
  encounter,
  hasCapability,
  statusOverride,
}: Pick<EncounterEnrichmentStatusBadgeProps, 'statusOverride'> & {
  currentUserId: string
  encounter: Pick<
    components['schemas']['EncounterAPIResponse'],
    'id' | 'flagged_for_joyful_followup_at' | 'assignee_biller_user_id'
  >
  hasCapability: HasCapability
}) {
  if (
    hasCapability(
      'encounter.enrichment_status_override.manage.non_assigned',
    ) === false &&
    encounter.assignee_biller_user_id !== currentUserId
  ) {
    return {
      disabled: true,
      tooltipContent:
        'You must be the queued biller for this encounter to apply status overrides.',
    }
  }

  const isFlaggedForJoyfulFollowup = isEncounterFlaggedForFollowUp(encounter)
  if (
    isFlaggedForJoyfulFollowup === false &&
    hasCapability(
      'encounter.enrichment_status_override.manage.non_flagged_for_joyful_followup',
    ) === false
  ) {
    return {
      disabled: true,
      tooltipContent:
        'Cannot manage status override until flagged for Joyful follow-up.',
    }
  }

  return {
    disabled: false,
    tooltipContent: statusOverride
      ? `Manage applied status override`
      : `Apply status override…`,
  }
}

export interface OverridableEncounterEnrichmentStatusBadgeProps extends Omit<
  EncounterEnrichmentStatusBadgeProps,
  'paidBy'
> {
  encounter: Pick<
    components['schemas']['EncounterAPIResponse'],
    | 'id'
    | 'flagged_for_joyful_followup_at'
    | 'assignee_biller_user_id'
    | 'workflow_status'
  >
}

export function OverridableEncounterEnrichmentStatusBadge({
  encounter,
  status,
  statusOverride,
  statusOverrideReason,
  ...badgeProps
}: OverridableEncounterEnrichmentStatusBadgeProps) {
  const currentUser = useCurrentUser()
  const hasCapability = useHasUserCapability()
  const orgSlug = useRouterOrgSlug()
  const selectedStatus = statusOverride ?? null
  const [applyingStatusOverride, setApplyingStatusOverride] = useState<
    components['schemas']['EncounterEnrichmentStatus'] | null
  >(null)
  const [isPopoverOpen, setPopoverOpen] = useState(false)
  const updateEncounter = useUpdateEncounterMutation({
    onMutate: () => {
      setPopoverOpen(false)
    },
    onSuccess: (_result, variables) => {
      if (
        (variables.enrichment_status_override === 'PAID' ||
          variables.enrichment_status_override === 'PATIENT_RESPONSIBILITY') &&
        isEncounterResolved(encounter) === false &&
        isEncounterClosed(encounter) === false &&
        hasCapability('encounter.action.resolve')
      ) {
        const toastId = toast.success(
          `Successfully applied status override. Would you like to resolve this encounter?`,
          {
            action: (
              <ButtonGroup className="w-auto" orientation="vertical">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    toast.dismiss(toastId)
                  }}
                >
                  Dismiss
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const encounterHref = getWorkspaceEncounterHref({
                      encounterId: encounter.id,
                      orgSlug,
                    })
                    const resolveHref = serializeEncounterRouteQueryParams(
                      encounterHref,
                      {
                        action: 'resolve',
                        sidebar: 'action',
                      },
                    )
                    globalThis.location.assign(resolveHref)
                    toast.dismiss(toastId)
                  }}
                >
                  {`Resolve…`}
                </Button>
              </ButtonGroup>
            ),
            duration: Infinity,
          },
        )
      }
    },
  })

  if (hasCapability('encounter.enrichment_status_override.manage') === false) {
    return (
      <EncounterEnrichmentStatusBadge
        paidBy={null}
        status={status}
        statusOverride={selectedStatus}
        statusOverrideReason={statusOverrideReason}
        {...badgeProps}
      />
    )
  }

  const rules = computeEnrichmentStatusOverrideEnabledRules({
    currentUserId: currentUser.id,
    encounter,
    hasCapability,
    statusOverride,
  })
  const isTriggerDisabled =
    rules.disabled ||
    (updateEncounter.isPending === true &&
      updateEncounter.context?.previousEncounter?.id === encounter.id)
  const isTriggerPending =
    updateEncounter.isPending === true &&
    statusOverride !== updateEncounter.variables.enrichment_status_override

  return (
    <Dialog
      open={applyingStatusOverride !== null}
      onOpenChange={() => {
        setApplyingStatusOverride(null)
      }}
    >
      <Popover
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (isTriggerDisabled) {
            return
          }

          setPopoverOpen(open)
        }}
      >
        <PopoverTrigger asChild>
          <Tooltip
            content={rules.tooltipContent}
            tooltipRootProps={{
              delayDuration: 750,
            }}
          >
            <EncounterEnrichmentStatusBadge
              paidBy={null}
              status={status}
              statusOverride={selectedStatus}
              statusOverrideReason={statusOverrideReason}
              isTooltipDisabled
              aria-disabled={isTriggerDisabled}
              startAdornment={
                isTriggerPending ? (
                  <SpinnerIcon className="size-3.5 shrink-0" />
                ) : undefined
              }
              {...badgeProps}
            />
          </Tooltip>
        </PopoverTrigger>

        <EnrichmentStatusOverridePopoverContent
          side="right"
          align="start"
          sideOffset={2}
          originalStatus={status}
          selectedStatus={selectedStatus}
          statusOverrideReason={statusOverrideReason}
          onSelectStatus={(enrichment_status_override) => {
            if (enrichment_status_override === null) {
              updateEncounter.mutate({
                encounterId: encounter.id,
                enrichment_status_override,
              })
            } else {
              setApplyingStatusOverride(enrichment_status_override)
            }
          }}
        />
      </Popover>

      <DialogContent asChild>
        <EncounterEnrichmentStatusOverrideReasonDialogContentForm
          targetStatus={applyingStatusOverride}
          onSubmit={(values) => {
            updateEncounter.mutate({
              encounterId: encounter.id,
              enrichment_status_override: applyingStatusOverride,
              enrichment_status_override_reason: values.reason,
            })
            setApplyingStatusOverride(null)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

const EncounterEnrichmentStatusOverrideReasonDialogContentFormSchema = z.object(
  {
    reason: z.string().min(1, {
      message: 'Reason is required',
    }),
  },
)

export function EncounterEnrichmentStatusOverrideReasonDialogContentForm({
  onSubmit,
  targetStatus,
  ...restProps
}: Omit<FormProps, 'onSubmit'> & {
  onSubmit: (
    values: z.infer<
      typeof EncounterEnrichmentStatusOverrideReasonDialogContentFormSchema
    >,
  ) => void
  targetStatus: components['schemas']['EncounterEnrichmentStatus'] | null
}) {
  const form = useForm({
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(
      EncounterEnrichmentStatusOverrideReasonDialogContentFormSchema,
    ),
  })

  return (
    <FormProvider {...form}>
      <Form
        {...restProps}
        onSubmit={form.handleSubmit(onSubmit)}
        shouldSubmitWithHotkey
      >
        <DialogHeader>
          <DialogTitle>
            Apply status override
            {targetStatus === null ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <>
                {` to `}
                <EncounterEnrichmentStatusBadge
                  paidBy={null}
                  status={targetStatus}
                  isTooltipDisabled
                  size="lg"
                />
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {targetStatus === null ? null : (
          <VisuallyHidden asChild>
            <DialogDescription>
              {`This will change the status of the encounter to ${EncounterEnrichmentStatusSchema.meta(targetStatus).title}.`}
            </DialogDescription>
          </VisuallyHidden>
        )}

        <DialogBody>
          <FormTextareaField
            control={form.control}
            label="Why are you applying this override?"
            name="reason"
            textareaProps={{
              placeholder: 'Enter reason…',
            }}
          />
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>

          <Button type="submit" variant="brand-subtle">
            {`Apply status override`}
          </Button>
        </DialogFooter>
      </Form>
    </FormProvider>
  )
}
